import {
  Award,
  BriefcaseBusiness,
  ClipboardCheck,
  Eye,
  Ghost,
  Handshake,
  Hourglass,
  Phone,
  Send,
  ThumbsDown,
  UserMinus,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export const JOB_APPLICATION_STATUSES = {
  INTERESTED: "interested",
  APPLIED: "applied",
  SCREENING: "screening",
  TECHNICAL: "technical",
  ONSITE: "onsite",
  TAKEHOME: "takehome",
  PENDING_OFFER: "pending_offer",
  OFFER_RECEIVED: "offer_received",
  OFFER_ACCEPTED: "offer_accepted",
  OFFER_DECLINED: "offer_declined",
  REJECTED: "rejected",
  WITHDRAWN: "withdrawn",
  GHOSTED: "ghosted",
} as const;

export type JobApplicationStatus =
  (typeof JOB_APPLICATION_STATUSES)[keyof typeof JOB_APPLICATION_STATUSES];

const INACTIVE_JOB_APPLICATION_STATUSES = new Set<JobApplicationStatus>([
  JOB_APPLICATION_STATUSES.OFFER_ACCEPTED,
  JOB_APPLICATION_STATUSES.OFFER_DECLINED,
  JOB_APPLICATION_STATUSES.REJECTED,
  JOB_APPLICATION_STATUSES.WITHDRAWN,
  JOB_APPLICATION_STATUSES.GHOSTED,
]);

type JobStatusMeta = {
  label: string;
  icon: LucideIcon;
  className: string;
  priority: number;
};

const STATUS_META: Record<JobApplicationStatus, JobStatusMeta> = {
  interested: {
    label: "Interested",
    icon: Eye,
    className: "bg-slate-100 text-slate-800 border-slate-200",
    priority: 70,
  },
  applied: {
    label: "Applied",
    icon: Send,
    className: "bg-blue-100 text-blue-800 border-blue-200",
    priority: 80,
  },
  screening: {
    label: "Screening",
    icon: Phone,
    className: "bg-cyan-100 text-cyan-800 border-cyan-200",
    priority: 90,
  },
  technical: {
    label: "Technical Interview",
    icon: ClipboardCheck,
    className: "bg-indigo-100 text-indigo-800 border-indigo-200",
    priority: 100,
  },
  onsite: {
    label: "On-site Interview",
    icon: BriefcaseBusiness,
    className: "bg-violet-100 text-violet-800 border-violet-200",
    priority: 110,
  },
  takehome: {
    label: "Take-home Assignment",
    icon: ClipboardCheck,
    className: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
    priority: 85,
  },
  pending_offer: {
    label: "Pending Offer",
    icon: Hourglass,
    className: "bg-amber-100 text-amber-800 border-amber-200",
    priority: 120,
  },
  offer_received: {
    label: "Offer Received",
    icon: Award,
    className: "bg-emerald-100 text-emerald-800 border-emerald-200",
    priority: 115,
  },
  offer_accepted: {
    label: "Offer Accepted",
    icon: Handshake,
    className: "bg-green-100 text-green-800 border-green-200",
    priority: 60,
  },
  offer_declined: {
    label: "Offer Declined",
    icon: ThumbsDown,
    className: "bg-rose-100 text-rose-800 border-rose-200",
    priority: 25,
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    className: "bg-red-100 text-red-800 border-red-200",
    priority: 10,
  },
  withdrawn: {
    label: "Withdrawn",
    icon: UserMinus,
    className: "bg-zinc-100 text-zinc-800 border-zinc-200",
    priority: 20,
  },
  ghosted: {
    label: "Ghosted",
    icon: Ghost,
    className: "bg-neutral-100 text-neutral-800 border-neutral-200",
    priority: 15,
  },
};

const UNKNOWN_STATUS_META: JobStatusMeta = {
  label: "Unknown",
  icon: Eye,
  className: "bg-gray-100 text-gray-800 border-gray-200",
  priority: 50,
};

export const formatStatusLabel = (status: string): string =>
  status
    .replace(/[_-]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const normalizeStatus = (
  status?: string | null
): JobApplicationStatus | undefined => {
  if (!status) return undefined;
  const lowered = status.toLowerCase() as JobApplicationStatus;
  return lowered in STATUS_META ? lowered : undefined;
};

export const getJobApplicationStatusMeta = (status?: string | null) => {
  const normalized = normalizeStatus(status);
  if (!status) {
    return { ...UNKNOWN_STATUS_META, label: "N/A", value: undefined };
  }
  if (!normalized) {
    return {
      ...UNKNOWN_STATUS_META,
      label: formatStatusLabel(status),
      value: status,
    };
  }
  return {
    ...STATUS_META[normalized],
    value: normalized,
  };
};

type JobStatusItem = {
  status?: string;
  created_at?: string;
  updated_at?: string;
};

type JobLike = {
  latest_status?: string | null;
  statuses?: JobStatusItem[];
  updated_at?: string;
  created_at?: string;
};

const asTime = (value?: string): number => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const getLatestJobApplicationStatus = (
  job: Pick<JobLike, "latest_status" | "statuses">
) => {
  if (job.latest_status) return job.latest_status;
  const statuses = Array.isArray(job.statuses) ? [...job.statuses] : [];
  if (statuses.length === 0) return null;
  statuses.sort(
    (a, b) => asTime(b.updated_at || b.created_at) - asTime(a.updated_at || a.created_at)
  );
  return statuses[0]?.status || null;
};

export const getStatusPriority = (status?: string | null) =>
  getJobApplicationStatusMeta(status).priority;

export const isInactiveJobApplicationStatus = (status?: string | null) => {
  const normalized = normalizeStatus(status);
  return normalized ? INACTIVE_JOB_APPLICATION_STATUSES.has(normalized) : false;
};

export const isActiveJobApplicationStatus = (status?: string | null) =>
  !isInactiveJobApplicationStatus(status);

export const isActiveJob = (job: Pick<JobLike, "latest_status" | "statuses">) =>
  isActiveJobApplicationStatus(getLatestJobApplicationStatus(job));

export const sortJobsByPriorityAndRecency = <T extends JobLike>(jobs: T[]) =>
  [...jobs].sort((a, b) => {
    const aStatus = getLatestJobApplicationStatus(a);
    const bStatus = getLatestJobApplicationStatus(b);
    const priorityDiff = getStatusPriority(bStatus) - getStatusPriority(aStatus);
    if (priorityDiff !== 0) return priorityDiff;

    return asTime(b.updated_at || b.created_at) - asTime(a.updated_at || a.created_at);
  });
